/**
 * HrAssetsTab — Módulo Activos asignables a empleados.
 * Tabs: Activos | Categorías | Reporte por empleado.
 */
import { todayISO } from '../../../lib/utils'
import { useState, useMemo } from 'react'
import { Loader2, Plus, Paperclip, UserPlus, RotateCcw, Trash2, Search } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { Autocomplete, type AutocompleteItem } from '@/components/Autocomplete'
import DatePicker from '@/components/DatePicker'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { PageSizeSelector, DEFAULT_PAGE_SIZE } from '@/components/PageSizeSelector'
import { useToast } from '@/components/Toast'
import { usePermission } from '@/hooks/usePermission'
import { useEmployees, type Employee } from '@/modules/payroll/hooks/usePayroll'
import {
  useAssetCategories,
  useCreateAssetCategory,
  useUpdateAssetCategory,
  useDeleteAssetCategory,
  useAssets,
  useAsset,
  useCreateAsset,
  useUpdateAsset,
  useDeleteAsset,
  useAssignAsset,
  useUnassignAsset,
  useAssetsByEmployee,
  type AssetListItem,
  type AssetStatus,
} from '../hooks/useHrAssets'
import { formatMoneyForTenant } from '@/lib/locale'
import { Pagination } from '@/components/ds/Pagination'
import { useTenantLocale } from '@/lib/locale'
import { EmptyState } from '@/components/EmptyState'
import { Modal } from '@/components/ds/Modal'

type Tab = 'list' | 'categories' | 'report'

const STATUS_LABELS: Record<AssetStatus, { label: string; cls: string }> = {
  available: { label: 'Disponible', cls: 'bg-success-surface text-success-content' },
  assigned:  { label: 'Asignado',   cls: 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300' },
  in_repair: { label: 'En reparación', cls: 'bg-warning-surface text-warning-content' },
  retired:   { label: 'Dado de baja', cls: 'bg-surface-muted text-content-muted' },
}

function fmtCLP(v: string | number): string {
  const n = typeof v === 'string' ? parseFloat(v) : v
  if (isNaN(n)) return '—'
  return formatMoneyForTenant(n)
}

export default function HrAssetsTab() {
  const [tab, setTab] = useState<Tab>('list')
  const { can } = usePermission()

  return (
    <div>
      <div className="flex items-center gap-1 border-b border-divider mb-4">
        {[
          { k: 'list' as const, label: 'Activos' },
          { k: 'categories' as const, label: 'Categorías' },
          { k: 'report' as const, label: 'Reporte por empleado' },
        ].map(t => (
          <button
            key={t.k}
            type="button"
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.k
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-content-muted hover:text-content'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'list' && <AssetsList canCreate={can('hr_assets', 'create')} canEdit={can('hr_assets', 'edit')} canDelete={can('hr_assets', 'delete')} canAssign={can('hr_assets', 'assign')} />}
      {tab === 'categories' && <CategoriesList canEdit={can('hr_assets', 'edit')} canCreate={can('hr_assets', 'create')} canDelete={can('hr_assets', 'delete')} />}
      {tab === 'report' && <ByEmployeeReport />}
    </div>
  )
}

// ─── Lista de activos ────────────────────────────────────────────────────────

function AssetsList({ canCreate, canEdit, canDelete, canAssign }: { canCreate: boolean; canEdit: boolean; canDelete: boolean; canAssign: boolean }) {
  const locale = useTenantLocale()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [createOpen, setCreateOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)

  const { data: categoriesData } = useAssetCategories({ active: 'true' })
  const { data, isLoading } = useAssets({
    page, page_size: pageSize, search: search || undefined,
    status: statusFilter || undefined, category: categoryFilter || undefined,
  })

  const assets = data?.assets ?? []
  const pages = data?.pages ?? 1

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-content-subtle" />
            <input aria-label="Buscar por nombre o serie"
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder="Buscar por nombre o serie…"
              className="pl-8 pr-3 py-1.5 text-sm border border-input-border rounded-lg bg-input-bg text-content w-64"
            />
          </div>
          <select aria-label="Todos los estados"
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
            className="text-sm border border-input-border rounded-lg px-2 py-1.5 bg-input-bg text-content"
          >
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select aria-label="Todas las categorías"
            value={categoryFilter}
            onChange={e => { setCategoryFilter(e.target.value); setPage(1) }}
            className="text-sm border border-input-border rounded-lg px-2 py-1.5 bg-input-bg text-content"
          >
            <option value="">Todas las categorías</option>
            {(categoriesData?.categories ?? []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover"
          >
            <Plus size={14} /> Nuevo activo
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="py-10 text-center text-sm text-content-subtle">Cargando…</div>
      ) : assets.length === 0 ? (
        <div className="py-10 text-center text-sm text-content-subtle">
          <Paperclip size={28} className="mx-auto mb-2 text-content-subtle" />
          No hay activos registrados.
        </div>
      ) : (
        <DataTable
          columns={[
            { key: 'name', header: 'Activo' },
            { key: 'category_name', header: 'Categoría' },
            { key: 'serial_number', header: 'Serie', render: a => <span className="font-mono text-xs">{a.serial_number || '—'}</span> },
            {
              key: 'status', header: 'Estado',
              render: a => {
                const st = STATUS_LABELS[a.status] ?? STATUS_LABELS.available
                return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
              },
            },
            { key: 'current_employee_name', header: 'Asignado a', render: a => a.current_employee_name || '—' },
            { key: 'purchase_cost', header: 'Costo', numeric: true, sortValue: a => Number(a.purchase_cost), render: a => fmtCLP(a.purchase_cost) },
          ]}
          rows={assets}
          rowKey={a => a.id}
          onRowClick={a => setDetailId(a.id)}
          minWidth={820}
          caption="Activos entregados al personal"
        />
      )}

      <div className="flex items-center justify-between mt-4 gap-4 flex-wrap">
        <PageSizeSelector
          value={pageSize}
          onChange={n => { setPageSize(n); setPage(1) }}
          storageKey="hr-assets-page-size"
        />
        {pages > 1 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data?.count ?? 0}
            onPageChange={setPage}
            locale={locale}
          />
        )}
      </div>

      {createOpen && <AssetCreateModal onClose={() => setCreateOpen(false)} />}
      {detailId && <AssetDetailModal id={detailId} onClose={() => setDetailId(null)} canEdit={canEdit} canDelete={canDelete} canAssign={canAssign} />}
    </div>
  )
}

// ─── Modal crear activo ──────────────────────────────────────────────────────

function AssetCreateModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [serial, setSerial] = useState('')
  const [purchaseDate, setPurchaseDate] = useState('')
  const [purchaseCost, setPurchaseCost] = useState('0')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const { data: categoriesData } = useAssetCategories({ active: 'true' })
  const create = useCreateAsset()
  const toast = useToast()

  const categoryItems = useMemo<AutocompleteItem[]>(
    () => (categoriesData?.categories ?? []).map(c => ({ id: c.id, label: c.name })),
    [categoriesData],
  )
  const selectedCategoryLabel = categoryItems.find(i => i.id === categoryId)?.label ?? ''

  const handleSubmit = async () => {
    setError(null)
    if (!name || !categoryId) {
      setError('Nombre y categoría son obligatorios.')
      return
    }
    try {
      await create.mutateAsync({
        name, category: categoryId, serial_number: serial,
        purchase_date: purchaseDate || null,
        purchase_cost: purchaseCost,
        notes,
      } as Partial<AssetListItem>)
      toast.success('Activo creado.')
      onClose()
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Error al crear el activo.')
    }
  }

  return (
    <Modal open onClose={onClose} title="Nuevo activo" size="lg" dismissOnScrimClick={false}
      actions={<>
        <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border border-divider rounded-lg hover:bg-soft">Cancelar</button>
        <button type="button" onClick={handleSubmit} disabled={create.isPending} className="px-3 py-1.5 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60">
          {create.isPending && <Loader2 size={12} className="inline animate-spin mr-1" />}
          Crear
        </button>
      </>}
    >
      <div className="space-y-3">
        {error && (
          <div className="bg-danger-surface border border-danger-border rounded-lg p-3 text-xs text-danger-content">{error}</div>
        )}
        <div>
          <label htmlFor="hrassetstab-nombre-modelo" className="text-xs font-medium text-content-muted">Nombre / Modelo *</label>
          <input id="hrassetstab-nombre-modelo" value={name} onChange={e => setName(e.target.value)} className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary" />
        </div>
        <div>
          <label htmlFor="hrassetstab-categoria" className="text-xs font-medium text-content-muted">Categoría *</label>
          <Autocomplete id="hrassetstab-categoria"
            items={categoryItems}
            value={categoryId}
            displayValue={selectedCategoryLabel}
            onChange={() => { /* selección vía onSelect */ }}
            onSelect={(item: AutocompleteItem) => setCategoryId(item.id)}
            placeholder="Buscar categoría…"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="hrassetstab-no-de-serie-imei" className="text-xs font-medium text-content-muted">Nº de serie / IMEI</label>
            <input id="hrassetstab-no-de-serie-imei" value={serial} onChange={e => setSerial(e.target.value)} className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div>
            <label htmlFor="hrassetstab-costo-de-adquisicion" className="text-xs font-medium text-content-muted">Costo de adquisición</label>
            <input id="hrassetstab-costo-de-adquisicion" type="number" min="0" step="0.01" value={purchaseCost} onChange={e => setPurchaseCost(e.target.value)} className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
        </div>
        <div>
          <label htmlFor="hrassetstab-fecha-de-adquisicion" className="text-xs font-medium text-content-muted">Fecha de adquisición</label>
          <DatePicker id="hrassetstab-fecha-de-adquisicion" value={purchaseDate} onChange={setPurchaseDate} />
        </div>
        <div>
          <label htmlFor="hrassetstab-notas-3" className="text-xs font-medium text-content-muted">Notas</label>
          <textarea id="hrassetstab-notas-3" value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary" />
        </div>
      </div>
    </Modal>
  )
}

// ─── Modal detalle / asignar ─────────────────────────────────────────────────

function AssetDetailModal({ id, onClose, canEdit, canDelete, canAssign }: { id: string; onClose: () => void; canEdit: boolean; canDelete: boolean; canAssign: boolean }) {
  const { data: asset, isLoading } = useAsset(id)
  const [assigning, setAssigning] = useState(false)
  const [unassigning, setUnassigning] = useState(false)
  const [employeeId, setEmployeeId] = useState('')
  const [assignDate, setAssignDate] = useState(todayISO())
  const [returnDate, setReturnDate] = useState(todayISO())
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const { data: employeesData } = useEmployees({ active: 'true', page_size: 9999 })
  const assign = useAssignAsset()
  const unassign = useUnassignAsset()
  const del = useDeleteAsset()
  const { confirm, modalProps } = useConfirmModal()
  const toast = useToast()

  const employeeItems = useMemo<AutocompleteItem[]>(
    () => ((employeesData?.employees ?? []) as Employee[]).map((e) => ({
      id: e.id,
      label: `${e.full_name || `${e.first_name} ${e.last_name}`} (${e.rut})`,
    })),
    [employeesData],
  )
  const selectedEmployeeLabel = employeeItems.find(i => i.id === employeeId)?.label ?? ''

  const handleAssign = async () => {
    if (!employeeId || !assignDate) { setError('Empleado y fecha son obligatorios.'); return }
    setError(null)
    try {
      await assign.mutateAsync({ id, employee_id: employeeId, assigned_at: assignDate, notes })
      toast.success('Activo asignado.')
      setAssigning(false); setEmployeeId(''); setNotes('')
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Error al asignar.')
    }
  }
  const handleUnassign = async () => {
    if (!returnDate) { setError('Fecha de devolución obligatoria.'); return }
    setError(null)
    try {
      await unassign.mutateAsync({ id, returned_at: returnDate, notes })
      toast.success('Activo devuelto.')
      setUnassigning(false); setNotes('')
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Error al desasignar.')
    }
  }
  const handleDelete = () => {
    confirm({
      message: '¿Eliminar este activo? Si tiene historial de asignaciones, se marca como dado de baja en su lugar.',
      variant: 'danger',
      confirmLabel: 'Eliminar',
      onConfirm: async () => {
        try { await del.mutateAsync(id); toast.success('Activo eliminado.'); onClose() }
        catch { toast.error('Error al eliminar.') }
      },
    })
  }

  return (
    <>
      <Modal open onClose={onClose} title={asset?.name || 'Activo'} size="2xl" dismissOnScrimClick={false}
        actions={<>
          {canDelete && asset && (
            <button type="button" onClick={handleDelete} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-danger-content-soft hover:bg-danger-surface rounded-lg">
              <Trash2 size={13} /> Eliminar
            </button>
          )}
          <button type="button" onClick={onClose} className="ml-auto px-3 py-1.5 text-sm border border-divider rounded-lg hover:bg-soft">Cerrar</button>
        </>}
      >
        <div className="flex-1 overflow-y-auto space-y-4">
          {isLoading || !asset ? (
            <p className="text-sm text-content-subtle">Cargando…</p>
          ) : (
            <>
              {error && (
                <div className="bg-danger-surface border border-danger-border rounded-lg p-3 text-xs text-danger-content">{error}</div>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-content-subtle text-xs">Categoría</span><div>{asset.category_name}</div></div>
                <div><span className="text-content-subtle text-xs">Estado</span><div><span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_LABELS[asset.status]?.cls}`}>{STATUS_LABELS[asset.status]?.label}</span></div></div>
                <div><span className="text-content-subtle text-xs">Nº de serie</span><div className="font-mono text-xs">{asset.serial_number || '—'}</div></div>
                <div><span className="text-content-subtle text-xs">Costo</span><div>{fmtCLP(asset.purchase_cost)}</div></div>
                <div><span className="text-content-subtle text-xs">Asignado a</span><div>{asset.current_employee_name || '—'}</div></div>
                <div><span className="text-content-subtle text-xs">Fecha compra</span><div>{asset.purchase_date || '—'}</div></div>
              </div>
              {asset.notes && (
                <div>
                  <span className="text-content-subtle text-xs">Notas</span>
                  <p className="text-sm text-content-muted whitespace-pre-wrap">{asset.notes}</p>
                </div>
              )}

              {/* Acciones de asignación */}
              {canAssign && asset.status !== 'retired' && (
                <div className="border-t border-divider pt-4">
                  {!assigning && !unassigning && (
                    <div className="flex gap-2">
                      {asset.current_employee_id ? (
                        <button type="button" onClick={() => setUnassigning(true)} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-warning-border text-warning-content rounded-lg hover:bg-warning-surface">
                          <RotateCcw size={13} /> Devolver
                        </button>
                      ) : (
                        <button type="button" onClick={() => setAssigning(true)} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
                          <UserPlus size={13} /> Asignar a empleado
                        </button>
                      )}
                    </div>
                  )}
                  {assigning && (
                    <div className="space-y-2 bg-glass-3 rounded-lg p-3">
                      <div>
                        <label htmlFor="hrassetstab-empleado" className="text-xs font-medium text-content-muted">Empleado *</label>
                        <Autocomplete id="hrassetstab-empleado"
                          items={employeeItems}
                          value={employeeId}
                          displayValue={selectedEmployeeLabel}
                          onChange={() => { /* selección vía onSelect */ }}
                          onSelect={(item: AutocompleteItem) => setEmployeeId(item.id)}
                          placeholder="Buscar empleado…"
                        />
                      </div>
                      <div>
                        <label htmlFor="hrassetstab-fecha-de-asignacion" className="text-xs font-medium text-content-muted">Fecha de asignación *</label>
                        <DatePicker id="hrassetstab-fecha-de-asignacion" value={assignDate} onChange={setAssignDate} />
                      </div>
                      <div>
                        <label htmlFor="hrassetstab-notas-2" className="text-xs font-medium text-content-muted">Notas</label>
                        <textarea id="hrassetstab-notas-2" rows={2} value={notes} onChange={e => setNotes(e.target.value)} className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary" />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => { setAssigning(false); setError(null) }} className="px-3 py-1.5 text-xs border border-divider rounded-lg hover:bg-soft">Cancelar</button>
                        <button type="button" onClick={handleAssign} disabled={assign.isPending} className="px-3 py-1.5 text-xs bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60">
                          {assign.isPending && <Loader2 size={12} className="inline animate-spin mr-1" />}
                          Confirmar
                        </button>
                      </div>
                    </div>
                  )}
                  {unassigning && (
                    <div className="space-y-2 bg-glass-3 rounded-lg p-3">
                      <div>
                        <label htmlFor="hrassetstab-fecha-de-devolucion" className="text-xs font-medium text-content-muted">Fecha de devolución *</label>
                        <DatePicker id="hrassetstab-fecha-de-devolucion" value={returnDate} onChange={setReturnDate} />
                      </div>
                      <div>
                        <label htmlFor="hrassetstab-notas" className="text-xs font-medium text-content-muted">Notas</label>
                        <textarea id="hrassetstab-notas" rows={2} value={notes} onChange={e => setNotes(e.target.value)} className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary" />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => { setUnassigning(false); setError(null) }} className="px-3 py-1.5 text-xs border border-divider rounded-lg hover:bg-soft">Cancelar</button>
                        <button type="button" onClick={handleUnassign} disabled={unassign.isPending} className="px-3 py-1.5 text-xs bg-warning-fill text-white rounded-lg hover:bg-warning-fill-hover disabled:opacity-60">
                          {unassign.isPending && <Loader2 size={12} className="inline animate-spin mr-1" />}
                          Confirmar devolución
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Historial */}
              {asset.assignments && asset.assignments.length > 0 && (
                <div className="border-t border-divider pt-4">
                  <p className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-2">Historial de asignaciones</p>
                  <DataTable
                    columns={[
                      { key: 'employee_name', header: 'Empleado' },
                      { key: 'assigned_at', header: 'Desde' },
                      {
                        key: 'returned_at', header: 'Hasta',
                        render: a => a.returned_at || <span className="text-success-content-soft">Vigente</span>,
                      },
                      {
                        key: 'notes', header: 'Notas',
                        render: a => <span className="block truncate max-w-[200px]">{a.notes || '—'}</span>,
                      },
                    ]}
                    rows={asset.assignments}
                    rowKey={a => a.id}
                    // Ya vive dentro del panel del detalle del activo.
                    surface={false}
                    minWidth={420}
                    caption="Historial de asignaciones del activo"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
      <ConfirmModal {...modalProps} />
    </>
  )
}

// ─── Categorías ──────────────────────────────────────────────────────────────

function CategoriesList({ canCreate, canEdit, canDelete }: { canCreate: boolean; canEdit: boolean; canDelete: boolean }) {
  const { data, isLoading } = useAssetCategories()
  const create = useCreateAssetCategory()
  const update = useUpdateAssetCategory()
  const del = useDeleteAssetCategory()
  const toast = useToast()
  const { confirm, modalProps } = useConfirmModal()
  const [newName, setNewName] = useState('')

  const handleCreate = async () => {
    if (!newName.trim()) return
    try { await create.mutateAsync({ name: newName.trim() }); setNewName(''); toast.success('Categoría creada.') }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Error al crear.') }
  }
  const handleDelete = (id: string, count: number) => {
    confirm({
      message: count > 0
        ? `Esta categoría tiene ${count} activo(s). Se desactivará en lugar de eliminarse.`
        : '¿Eliminar esta categoría?',
      variant: count > 0 ? 'warning' : 'danger',
      confirmLabel: count > 0 ? 'Desactivar' : 'Eliminar',
      onConfirm: async () => {
        try { await del.mutateAsync(id); toast.success('Categoría procesada.') }
        catch { toast.error('Error al procesar.') }
      },
    })
  }

  return (
    <>
      {canCreate && (
        <div className="flex gap-2 mb-4 max-w-md">
          <input aria-label="Nombre del nuevo activo"
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate() }}
            placeholder="Nombre de la nueva categoría…"
            className="flex-1 border border-input-border rounded-lg px-3 py-1.5 text-sm bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button type="button" onClick={handleCreate} disabled={!newName.trim() || create.isPending} className="px-3 py-1.5 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60">
            {create.isPending && <Loader2 size={12} className="inline animate-spin mr-1" />}
            Agregar
          </button>
        </div>
      )}
      {isLoading ? (
        <p className="text-sm text-content-subtle">Cargando…</p>
      ) : !data?.categories.length ? (
        <p className="text-sm text-content-subtle">Sin categorías. Crea la primera arriba.</p>
      ) : (
        <div className="max-w-2xl">
          <DataTable
            columns={[
              { key: 'name', header: 'Nombre' },
              { key: 'asset_count', header: 'Activos', numeric: true },
              {
                key: 'is_active', header: 'Estado',
                render: c => c.is_active
                  ? <span className="text-success-content-soft text-xs">Activa</span>
                  : <span className="text-content-subtle text-xs">Inactiva</span>,
              },
              {
                key: 'acciones', header: '',
                render: c => (
                  <div className="text-right">
                    {canEdit && !c.is_active && (
                      <button type="button" onClick={() => update.mutate({ id: c.id, is_active: true })} className="text-xs text-primary hover:text-primary-hover underline mr-2">Reactivar</button>
                    )}
                    {canDelete && c.is_active && (
                      <button type="button" onClick={() => handleDelete(c.id, c.asset_count)} className="text-xs text-danger-content-soft hover:underline">
                        {c.asset_count > 0 ? 'Desactivar' : 'Eliminar'}
                      </button>
                    )}
                  </div>
                ),
              },
            ]}
            rows={data.categories}
            rowKey={c => c.id}
            minWidth={420}
            caption="Categorías de activos"
          />
        </div>
      )}
      <ConfirmModal {...modalProps} />
    </>
  )
}

// ─── Reporte por empleado ────────────────────────────────────────────────────

function ByEmployeeReport() {
  const { data, isLoading } = useAssetsByEmployee()
  const grandTotal = useMemo(() => (data?.rows ?? []).reduce((s, r) => s + parseFloat(r.total_cost || '0'), 0), [data])

  if (isLoading) return <p className="text-sm text-content-subtle">Cargando…</p>
  if (!data?.rows.length) return <EmptyState title="No hay activos asignados actualmente." size="sm" />

  return (
    <div>
      <div className="mb-3 text-sm text-content-muted">
        {data.count} empleado{data.count !== 1 ? 's' : ''} con activos asignados — total: <span className="font-semibold text-content">{fmtCLP(grandTotal)}</span>
      </div>
      <div className="space-y-3">
        {data.rows.map(row => (
          <div key={row.employee_id} className="border border-divider rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-surface-muted border-b border-divider">
              <span className="font-medium text-content">{row.employee_name}</span>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-content-muted">{row.count} activo{row.count !== 1 ? 's' : ''}</span>
                <span className="font-semibold text-content tabular-nums">{fmtCLP(row.total_cost)}</span>
              </div>
            </div>
            {/* ⚠️ EXENTA del barrido al DataTable, a propósito: no tiene <thead>.
                Es la lista de activos de UN empleado dentro de su tarjeta, no una
                tabla de datos — migrarla le agregaría encabezados de columna que
                hoy no tiene, o sea cambiaría la pantalla para peor. Mismo criterio
                que FirmAdminDetailModal. */}
            <table className="w-full text-xs">
              <tbody className="divide-y divide-divider">
                {row.assets.map(a => (
                  <tr key={a.id}>
                    <td className="px-4 py-1.5 text-content-muted">{a.category_name}</td>
                    <td className="px-4 py-1.5 text-content">{a.name}</td>
                    <td className="px-4 py-1.5 text-content-subtle">desde {a.assigned_at}</td>
                    <td className="px-4 py-1.5 text-right text-content tabular-nums">{fmtCLP(a.purchase_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  )
}
